import React, { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CircularProgress } from '@mui/material';
import { Button } from '../../components/UI/Button/Button';
import { useApi } from '../../hooks/useApi';
import { useStores } from '../../hooks/useStore';
import { Course, CourseModule, Lesson } from '../../models/courses';

import LessonForm from '../../components/popUp/Courses/LessonForm';
import styles from './courses.module.css';

interface SortableLessonRowProps {
  lesson: Lesson;
  onEdit: (_l: Lesson) => void;
  onDelete: (_l: Lesson) => void;
  onOpen: (_l: Lesson) => void;
}

const SortableLessonRow: React.FC<SortableLessonRowProps> = ({
  lesson,
  onEdit,
  onDelete,
  onOpen,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lesson.id });

  const rowStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={rowStyle} className={styles.moduleRow}>
      <span
        className={styles.dragHandle}
        {...attributes}
        {...listeners}
        aria-label="Перетащить"
        role="button"
        tabIndex={0}
      >
        <svg
          width="16"
          height="22"
          viewBox="0 0 16 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="5" cy="4" r="1.5" fill="currentColor" />
          <circle cx="11" cy="4" r="1.5" fill="currentColor" />
          <circle cx="5" cy="11" r="1.5" fill="currentColor" />
          <circle cx="11" cy="11" r="1.5" fill="currentColor" />
          <circle cx="5" cy="18" r="1.5" fill="currentColor" />
          <circle cx="11" cy="18" r="1.5" fill="currentColor" />
        </svg>
      </span>

      <div className={styles.moduleRowInfo} onClick={() => onOpen(lesson)}>
        <span className={styles.moduleRowTitle}>{lesson.title}</span>
        <span className={styles.moduleRowDesc}>
          {lesson.duration_minutes ? `${lesson.duration_minutes} мин` : '—'}
        </span>
      </div>

      <div className={styles.moduleRowMeta}>
        {lesson.is_preview && (
          <span className={styles.badgePublished}>Превью</span>
        )}
      </div>

      <div
        className={styles.moduleRowActions}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.iconBtn}
          aria-label="Открыть"
          onClick={() => onOpen(lesson)}
          title="Открыть редактор"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          aria-label="Редактировать"
          onClick={() => onEdit(lesson)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
          </svg>
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          aria-label="Удалить"
          onClick={() => onDelete(lesson)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

interface ModuleLessonsProps {
  course: Course;
  module: CourseModule;
  onSelectLesson: (_lesson: Lesson) => void;
  onBack: () => void;
  onBackToCourses: () => void;
}

export const ModuleLessons: React.FC<ModuleLessonsProps> = ({
  course,
  module,
  onSelectLesson,
  onBack,
  onBackToCourses,
}) => {
  const api = useApi();
  const store = useStores();

  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [openLessonForm, setOpenLessonForm] = useState<boolean>(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  useEffect(() => {
    loadLessons();
    // eslint-disable-next-line
  }, []);

  const loadLessons = async () => {
    try {
      setLoading(true);
      const { body } = await api.courses.getLessons(module.id);
      if (body) {
        setLessons([...body].sort((a, b) => a.sort_order - b.sort_order));
      }
    } catch {
      store.notificationsStore.setError('Ошибка при загрузке уроков');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (lesson: Lesson) => {
    const confirmed = window.confirm(`Удалить урок "${lesson.title}"?`);
    if (!confirmed) {
      return;
    }

    try {
      store.notificationsStore.showPreloader();
      await api.courses.deleteLesson(lesson.id);
      await loadLessons();
    } catch {
      store.notificationsStore.setError('Ошибка при удалении урока');
    } finally {
      store.notificationsStore.hidePreloader();
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = lessons.findIndex((l) => l.id === active.id);
    const newIndex = lessons.findIndex((l) => l.id === over.id);
    const reordered = arrayMove(lessons, oldIndex, newIndex);
    setLessons(reordered);

    try {
      await api.courses.reorderLessons(module.id, {
        ids: reordered.map((l) => l.id),
      });
    } catch {
      store.notificationsStore.setError('Ошибка при изменении порядка уроков');
      setLessons(lessons);
    }
  };

  const handleEditLesson = (lesson: Lesson) => {
    setSelectedLesson(lesson);
    setOpenLessonForm(true);
  };

  const handleCloseForm = () => {
    setOpenLessonForm(false);
    setSelectedLesson(null);
  };

  const handleFormSuccess = () => {
    handleCloseForm();
    loadLessons();
  };

  return (
    <>
      <LessonForm
        open={openLessonForm}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        moduleId={module.id}
        lesson={selectedLesson}
      />
      <div className={styles.container}>
        <div className={styles.pageName}>
          <div className={styles.top}>
            <div className={styles.title}>
              <h2>
                <span
                  onClick={onBackToCourses}
                  style={{ cursor: 'pointer', opacity: 0.5 }}
                >
                  Курсы
                </span>
                {' > '}
                <span
                  onClick={onBack}
                  style={{ cursor: 'pointer', opacity: 0.5 }}
                >
                  {course.title}
                </span>
                {' > '}
                {module.title}
              </h2>
            </div>
            <Button
              style={{ maxWidth: 180 }}
              appearance="auth"
              onClick={() => {
                setSelectedLesson(null);
                setOpenLessonForm(true);
              }}
            >
              Добавить урок
            </Button>
          </div>
        </div>

        {loading ? (
          <CircularProgress />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={lessons.map((l) => l.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className={styles.moduleList}>
                {lessons.map((lesson) => (
                  <SortableLessonRow
                    key={lesson.id}
                    lesson={lesson}
                    onEdit={handleEditLesson}
                    onDelete={handleDelete}
                    onOpen={onSelectLesson}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </>
  );
};
